import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import './HomePage.css';

function getGreeting(name) {
  const hour = new Date().getHours();
  const firstName = name ? name.split(' ')[0] : '';

  if (hour >= 5 && hour < 12) {
    return {
      salute: 'Selamat Pagi',
      name: firstName,
      sub: 'Semoga hari anda dipenuhi keberkatan',
      period: 'pagi',
    };
  }
  if (hour >= 12 && hour < 15) {
    return {
      salute: 'Selamat Tengah Hari',
      name: firstName,
      sub: 'Jangan lupa makan tengah hari',
      period: 'tengahhari',
    };
  }
  if (hour >= 15 && hour < 19) {
    return {
      salute: 'Selamat Petang',
      name: firstName,
      sub: 'Nikmati petang yang indah ini',
      period: 'petang',
    };
  }
  return {
    salute: 'Selamat Malam',
    name: firstName,
    sub: 'Rehat yang cukup untuk esok',
    period: 'malam',
  };
}

function HomePage() {
  const [vouchers, setVouchers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [now, setNow] = useState(new Date());
  const greetRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('spe_customer');
      if (saved) setCustomer(JSON.parse(saved));
    } catch {}

    let registered = [];
    try {
      registered = JSON.parse(localStorage.getItem('spe_registered_campaigns') || '[]');
    } catch {}

    const loaded = registered
      .map(r => {
        try {
          const data = JSON.parse(localStorage.getItem(`spe_voucher_${r.uuid}`));
          if (data) return { ...data, campaign_uuid: r.uuid, campaign_name: data.campaign_name || r.name };
        } catch {}
        return null;
      })
      .filter(Boolean);

    setVouchers(loaded);

    // Trigger reveal animation
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  // Realtime clock — update every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const greeting = getGreeting(customer?.full_name);
  const timeStr = now.toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('ms-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // No customer — fullscreen glassy empty state
  if (!customer) {
    return (
      <div className="home-page">
        <div className="hero-empty">
          <div className="hero-empty-glass">
            <div className="hero-empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <h1 className="hero-empty-title">
              Selamat<br />Datang
            </h1>
            <p className="hero-empty-desc">
              Sertai kempen menarik dan nikmati tawaran eksklusif daripada kami
            </p>
            <NavLink to="/check" className="glass-btn">
              <span>Semak Voucher</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  // Customer exists — personalized glassy greeting
  return (
    <div className="home-page">
      {/* Greeting hero */}
      <div className={`greeting-hero greeting-${greeting.period} ${revealed ? 'revealed' : ''}`} ref={greetRef}>
        <div className="greeting-orb orb-a" />
        <div className="greeting-orb orb-b" />
        <div className="greeting-orb orb-c" />

        <div className="greeting-content">
          <div className="greeting-meta">
            <span className="greeting-time">{timeStr}</span>
            <span className="greeting-dot" />
            <span className="greeting-date">{dateStr}</span>
          </div>

          <h1 className="greeting-salute">{greeting.salute}</h1>
          {greeting.name && <h2 className="greeting-name">{greeting.name}</h2>}

          <p className="greeting-sub">{greeting.sub}</p>

          {vouchers.length > 0 && (
            <div className="greeting-stat-pill">
              <span className="stat-dot" />
              {vouchers.length} voucher aktif
            </div>
          )}
        </div>
      </div>

      {/* Notice: update customer info */}
      {vouchers.length > 0 && (!customer.email || !customer.id_number) && (
        <NavLink to="/profile" className="info-notice">
          <div className="info-notice-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="info-notice-text">
            <strong>Lengkapkan maklumat anda</strong>
            <span>Kemaskini profil sebelum voucher boleh ditebus</span>
          </div>
          <svg className="info-notice-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </NavLink>
      )}

      {/* Voucher list */}
      {vouchers.length > 0 && (
        <div className="voucher-section">
          <div className="section-label">
            <span className="section-line" />
            <span>Voucher Saya</span>
            <span className="section-line" />
          </div>

          <div className="voucher-list">
            {vouchers.map((v, i) => (
              <NavLink
                key={v.code || i}
                to={`/${v.campaign_uuid}`}
                className={`voucher-card ${revealed ? 'revealed' : ''}`}
                style={{ transitionDelay: `${0.3 + i * 0.1}s` }}
              >
                <div className="voucher-card-glow" />
                <div className="voucher-card-inner">
                  <div className="voucher-card-left">
                    <div className="voucher-campaign-name">{v.campaign_name}</div>
                    <div className="voucher-code">{v.code}</div>
                    {v.created_at && (
                      <div className="voucher-date">
                        {new Date(v.created_at).toLocaleDateString('ms-MY', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </div>
                    )}
                  </div>
                  <div className="voucher-card-right">
                    {v.campaign_type === 'discount' && v.discount_percentage && (
                      <div className="voucher-value">{v.discount_percentage}<span>%</span></div>
                    )}
                    {v.campaign_type === 'cashback' && v.cashback_amount && (
                      <div className="voucher-value"><span>RM</span>{v.cashback_amount}</div>
                    )}
                    {v.campaign_type === 'freebie' && v.reward_title && (
                      <div className="voucher-value-text">{v.reward_title}</div>
                    )}
                    {!['discount', 'cashback', 'freebie'].includes(v.campaign_type) && (
                      <div className="voucher-badge-pill">Voucher</div>
                    )}
                    <div className="voucher-arrow">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Branches */}
                {v.branches && v.branches.length > 0 && (
                  <div className="voucher-branches">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>
                      {v.all_branches
                        ? 'Semua cawangan'
                        : v.branches.map(b => b.name).join(', ')}
                    </span>
                  </div>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      <div className="home-actions">
        <NavLink to="/check" className="glass-btn-outline">
          Semak Voucher Lain
        </NavLink>
      </div>
    </div>
  );
}

export default HomePage;
